<template>
  <div>
    <b-card>
      <h4>Список направлений вуза</h4>
      <b-alert v-if="isContentManager && !partners.length" show variant="info">
        Для Вашей организации не предусмотрена возможность изменения направлений обучения
      </b-alert>
      <div v-else>
        <h5>Вуз-партнер</h5>
        <MultiSelect
          required
          :value="partner"
          :options="partners"
          label="title"
          :allowEmpty="false"
          @input="updateDataPartner"
        />
      </div>
      <div class="d-flex flex-wrap mt-4">
        <h5>Направления</h5>
        <div class="ml-auto">
          <b-button
            variant="primary"
            @click="onAddFromCsv"
            :disabled="!partner || !partner.length"
          >
            Вставить
          </b-button>
          <b-button
            variant="primary"
            class="ml-2"
            @click="onAddDirectionUniversity"
            :disabled="!partner || !partner.length"
          >
            Добавить
          </b-button>
        </div>
      </div>
      <div class="ag-grid-olympics-list mt-2" ref="gridDiv">
        <ag-grid-vue
          class="ag-theme-material h-100"
          :gridOptions="gridOptions"
          :columnDefs="columnDefs"
          :rowData="rowData"
        >
        </ag-grid-vue>
      </div>
    </b-card>
    <DeleteModal
      id="deleteDirectionUniversityModal"
      title="Подтверждение удаления направления вуза"
      @delete="onDeleteConfirm"
    >
      {{ deleteText }}
    </DeleteModal>
    <EditDirectionUniversityModal :editData="selected" @save="onSaveDirectionUniversity" />
    <CopyUniversityDirectionModal
      :selected="selected"
      :existTypes="existTypes"
      @save="onCopyDirection"
    />
    <PasteFromExcelDirectionModal @paste="getPasteData" />
    <ChooseCorrectPasteModal
      :olympiads="dicts.olympiads"
      :directions="dicts.directions"
      :exams="dicts.exams"
      :incorrectData="incorrectData"
      @save="updateCorrectData"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { AgGridVue } from 'ag-grid-vue'
import AgGridFactory from '@/components/helpers/factory/agGrid'
import DeleteModal from '@/components/ui/modals/DeleteModal.vue'
import MultiSelect from '@/components/ui/MultiSelect.vue'
import EditDirectionUniversityModal from '@/components/admin/modals/recommendations/EditDirectionUniversityModal.vue'
import RecommendationAPI from '@/api/recommendation'
import { studyingForms } from '@/config/studying-forms'
import CopyUniversityDirectionModal from '@/components/admin/modals/recommendations/CopyUniversityDirectionModal.vue'
import PasteFromExcelDirectionModal from '@/components/admin/modals/recommendations/PasteFromExcelDirectionModal.vue'
import ChooseCorrectPasteModal from '@/components/admin/modals/recommendations/ChooseCorrectPasteModal.vue'
import { mapActions, mapGetters } from 'vuex'
import { roles } from '@/config/roles'

export default {
  name: 'DirectionUniversity',
  components: {
    AgGridVue,
    DeleteModal,
    EditDirectionUniversityModal,
    CopyUniversityDirectionModal,
    PasteFromExcelDirectionModal,
    ChooseCorrectPasteModal,
    MultiSelect
  },
  data() {
    return {
      gridOptions: AgGridFactory.getGridOptionsDefault(),
      columnDefs: [
        {
          headerName: 'Код',
          field: 'educationDirection.code',
          suppressSizeToFit: true,
          width: 120
        },
        {
          headerName: 'Название',
          field: 'educationDirection.title',
          minWidth: 100,
          width: 350
        },
        {
          headerName: 'Тип',
          field: 'studyingForm',
          valueFormatter: (params) => {
            const form = studyingForms.find((sf) => sf.value === params.value)
            return form ? form.short : '?'
          },
          suppressSizeToFit: true,
          width: 120
        },
        {
          headerName: 'Доп. баллы',
          field: 'recomOlympiads',
          filter: false,
          valueFormatter: (params) => (params.value && params.value.length ? 'Да' : 'Нет'),
          suppressSizeToFit: true,
          width: 120
        },
        AgGridFactory.getDefaultActionRenderer({
          onEdit: this.onEditClick,
          onDelete: this.onDeleteClick,
          onCopy: this.onCopy
        })
      ],
      rowData: [],
      deleteText: 'При удалении направления вуза будет также удалена '
        + 'связанная с ним информация. Вы подтверждаете удаление?',
      selected: null,
      existTypes: [],
      partner: null,
      partners: [],
      dicts: {
        olympiads: [],
        exams: [],
        directions: []
      },
      incorrectData: null,
      dataForPaste: null
    }
  },
  async created() {
    const [universities, olympiads, directions, exams] = await Promise.all([
      RecommendationAPI.getUniversities(),
      RecommendationAPI.getOlympiads(),
      RecommendationAPI.getDirectionsByGroup(),
      RecommendationAPI.getExamSubjects()
    ])
    this.dicts = {
      olympiads: olympiads.data,
      exams: exams.data,
      directions: directions.data
        .map((d) => ({ ...d, codeTitle: `${d.code} ${d.title}` }))
    }

    this.partners = this.isContentManager ? 
      universities.data.filter((org) => org.id == this.currUser.organizationId) : 
      _.sortBy(universities.data, 'title')

    if (this.partners.length) {
      this.partner = [this.partners[0]]
      await this.updateDataPartner(this.partners[0])
    }
  },
  methods: {
    ...mapActions({
      pushToasts: 'toasts/pushToasts'
    }),
    async updateDataPartner(obj) {
      const oldPartner = _.cloneDeep(this.partner)

      const filterModel = this.gridOptions.api ? this.gridOptions.api.getFilterModel() : null
      const sortModel = this.gridOptions.api ? this.gridOptions.api.getSortModel() : null
      this.partner = [obj]
      const response = await RecommendationAPI.getUniversityDirections(obj.id)
      this.rowData = _.sortBy(response.data, ['educationDirection.code', 'studyingForm'])
      const isPartnerChanged = oldPartner && _.isEqual(oldPartner[0], obj)
      this.$nextTick(() => {
        if (this.gridOptions.api) {
          this.gridOptions.api.setFilterModel(isPartnerChanged ? filterModel : null)
          this.gridOptions.api.setSortModel(isPartnerChanged ? sortModel : null)
        }
      })
    },
    onEditClick(params) {
      this.selected = {
        ...params,
        egeSubjects: _.orderBy(params.egeSubjects, ['directionUniversitySubject.order'], ['asc']),
        directionMarkHistories: _.orderBy(params.directionMarkHistories, ['year'], ['desc'])
      }
      this.$bvModal.show('editDirectionUniversityModal')
    },
    onDeleteClick(params) {
      this.selected = params
      this.$bvModal.show('deleteDirectionUniversityModal')
    },
    onAddDirectionUniversity() {
      this.selected = null
      this.$bvModal.show('editDirectionUniversityModal')
    },
    onAddFromCsv() {
      this.$bvModal.show('pasteDirectionFromExcelModal')
    },
    onCopy(params) {
      this.selected = params
      this.existTypes = this.rowData
        .filter((rd) => rd.educationDirection.id === params.educationDirection.id)
        .map((rd) => rd.studyingForm)
      this.$bvModal.show('cloneUniversityDirectionModal')
    },
    async onSaveMarks(params) {
      await RecommendationAPI.updateUniversityDirections(this.partner[0].id, {
        ...this.selected,
        directionMarkHistories: params
      })
      await this.updateDataPartner(this.partner[0])
    },
    async onDeleteConfirm() {
      await RecommendationAPI.deleteUniversityDirections(this.partner[0].id, this.selected.id)
      await this.updateDataPartner(this.partner[0])
    },
    async onSaveDirectionUniversity(params) {
      await RecommendationAPI.updateUniversityDirections(this.partner[0].id, {
        ...this.selected,
        ...params,
        directionId: params.educationDirection.id
      })
      await this.updateDataPartner(this.partner[0])
    },
    async onCopyDirection(params) {
      for (const type of params) {
        // eslint-disable-next-line no-await-in-loop
        await RecommendationAPI.updateUniversityDirections(this.partner[0].id, {
          ...this.selected,
          id: null,
          studyingForm: type
        })
      }
      await this.updateDataPartner(this.partner[0])
    },
    getPasteData(data) {
      const errors = []
      let directionIncorrect = null
      const olympiadsIncorrect = []
      const examsIncorrect = []
      const direction = this.dicts.directions.find((d) => d.code === data.code.trim())
      if (!direction) {
        directionIncorrect = {
          current: data.code,
          correct: null
        }
        errors.push('Данное направление не найдено')
      }
      for (const olympiad of data.olympiad) {
        const olympiadInfo = this.dicts.olympiads.find((ol) => ol.title.toLowerCase()
          === olympiad.title.trim().toLowerCase()
          || ol.title.toLowerCase().includes(olympiad.title.trim().toLowerCase()))
        if (!olympiadInfo) {
          olympiadsIncorrect.push({
            current: olympiad.title,
            correct: null
          })
          errors.push(`Не найдена олимпиада - ${olympiad.title}`)
        }
      }
      const egeFromAllTypes = _.uniqBy([
        ...data.budget.egeSubjects,
        ...data.contract.egeSubjects,
        ...data.target.egeSubjects
      ], 'name')
      for (const exams of egeFromAllTypes) {
        for (const exam of exams.name.split('/')) {
          const examInfo = this.dicts.exams.find((ex) => ex.name.toLowerCase()
            === exam.trim().toLowerCase())
          if (!examInfo) {
            examsIncorrect.push({
              current: exam,
              correct: null
            })
            errors.push(`Не найден предмет - ${exam}`)
          }
        }
      }
      this.dataForPaste = data
      if (errors.length) {
        this.incorrectData = {
          direction: directionIncorrect,
          olympiads: olympiadsIncorrect,
          exams: examsIncorrect
        }
        this.$bvModal.show('chooseCorrectPasteModal')
      } else {
        this.addDirectionsByPaste()
      }
    },
    updateCorrectData(data) {
      this.addDirectionsByPaste(data)
    },
    async addDirectionsByPaste(fixedData) {
      const direction = this.dicts.directions.find((d) => d.code === this.dataForPaste.code.trim())
      const directionId = direction ? direction.id : fixedData.direction.correct[0].id
      const alreadyExistsDirections = this.rowData
        .filter((d) => d.educationDirection.id === directionId)
      const saveObjects = []
      for (const type of studyingForms.map((f) => f.value)) {
        const yearInfo = _.maxBy(this.dataForPaste[type].directionMarkHistories, 'year')
        if (yearInfo.quota) {
          const sameDirection = alreadyExistsDirections.find((d) => d.studyingForm === type)
          const objectForAdd = sameDirection
            ? {
              ...sameDirection,
              egeSubjects: [],
              recomOlympiads: []
            }
            : {
              directionId,
              studyingForm: type,
              egeSubjects: [],
              directionMarkHistories: [],
              recomOlympiads: []
            }
          for (const exams of this.dataForPaste[type].egeSubjects) {
            for (const exam of exams.name.split('/')) {
              const examInfo = this.dicts.exams.find((ex) => ex.name.toLowerCase()
                === exam.trim()
                  .toLowerCase())
              const subjectId = examInfo
                ? examInfo.id
                : fixedData.exams.find((ex) => ex.current === exam).correct[0].id
              objectForAdd.egeSubjects.push({
                id: subjectId,
                directionUniversitySubject: {
                  minimalMark: exams.minimalMark,
                  order: exams.order,
                  isProfile: false
                }
              })
            }
          }
          for (const history of this.dataForPaste[type].directionMarkHistories) {
            const alreadyYearIndex = objectForAdd.directionMarkHistories
              .findIndex((y) => y.year === history.year)
            if (alreadyYearIndex > -1) {
              objectForAdd.directionMarkHistories[alreadyYearIndex] = {
                year: history.year,
                quota: history.quota || objectForAdd.directionMarkHistories[alreadyYearIndex].quota,
                mark: history.mark || objectForAdd.directionMarkHistories[alreadyYearIndex].mark
              }
            } else {
              objectForAdd.directionMarkHistories.push(history)
            }
          }
          for (const olympiad of this.dataForPaste.olympiad) {
            const olympiadInfo = this.dicts.olympiads.find((ol) => ol.title.toLowerCase()
              === olympiad.title.trim()
                .toLowerCase()
              || ol.title.toLowerCase()
                .includes(olympiad.title.trim()
                  .toLowerCase()))
            const olympiadId = olympiadInfo
              ? olympiadInfo.id
              : fixedData.olympiads.find((ol) => ol.current === olympiad.title).correct[0].id
            objectForAdd.recomOlympiads.push({
              id: olympiadId,
              olympiadDirection: {
                addMark: olympiad.mark
              }
            })
          }
          saveObjects.push(objectForAdd)
        }
      }
      try {
        await Promise.all(saveObjects.map((object) => RecommendationAPI
          .updateUniversityDirections(this.partner[0].id, object)))
        this.pushToasts({
          time: 5,
          title: 'Уведомление',
          message: `Успешно обновлены направления - ${this.dataForPaste.code}`
        })
      } catch (e) {
        const studyingForm = studyingForms
          .find((f) => f.value === JSON.parse(e.response.config.data).studyingForm)
        this.pushToasts({
          error: true,
          time: 5,
          title: 'Ошибка',
          message: `Не удалось добавить направление ${this.dataForPaste.code} (${studyingForm.text})`
        })
      }
      await this.updateDataPartner(this.partner[0])
    }
  },
  computed: {
    ...mapGetters({
      hasRole: 'currentUser/hasRole',
      currUser: 'currentUser/getCurrentUser',
    }),
    isContentManager() {
      return this.hasRole(roles.content) && !this.hasRole(roles.admin)
    }
  }
}
</script>

<style scoped>
.ag-grid-olympics-list {
  height: calc(100vh - 380px);
}
</style>
